import { type FC, useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

interface CommandBarProps {
  onParsedCommand: (parsed: ParsedCommand) => void;
  onExecute: () => void;
  selectedTokenSymbol?: string;
}

export interface ParsedCommand {
  tokenQuery?: string;
  capitalSol?: number;
  leverage?: number;
  priority?: 'normal' | 'fast' | 'turbo';
  action?: 'long' | 'close' | 'closeall';
}

export const CommandBar: FC<CommandBarProps> = ({
  onParsedCommand,
  onExecute,
  selectedTokenSymbol,
}) => {
  const [value, setValue] = useState('');
  const [focused, setFocused] = useState(false);
  const [lastParsed, setLastParsed] = useState<ParsedCommand>({});
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        inputRef.current?.focus();
      }

      if (event.key === 'Escape' && focused) {
        inputRef.current?.blur();
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [focused]);

  const parseCommand = useCallback((input: string): ParsedCommand => {
    const lower = input.toLowerCase().trim();
    const result: ParsedCommand = {};

    if (lower.startsWith('close all') || lower.startsWith('closeall')) {
      return { action: 'closeall' };
    }

    if (lower.startsWith('close')) {
      return { action: 'close' };
    }

    result.action = 'long';

    const solMatch = lower.match(/(\d+\.?\d*)\s*sol/);
    if (solMatch) {
      result.capitalSol = parseFloat(solMatch[1]);
    }

    const levMatch = lower.match(/(\d+)\s*x/);
    if (levMatch) {
      result.leverage = parseInt(levMatch[1], 10);
    }

    if (lower.includes('turbo')) result.priority = 'turbo';
    else if (lower.includes('fast')) result.priority = 'fast';
    else if (lower.includes('normal') || lower.includes('slow')) result.priority = 'normal';

    const keywords = new Set([
      'long',
      'short',
      'buy',
      'sell',
      'open',
      'sol',
      'at',
      'on',
      'with',
      'fast',
      'turbo',
      'normal',
      'close',
    ]);

    for (const word of input.split(/\s+/)) {
      const clean = word.replace(/[^a-zA-Z]/g, '');
      if (clean.length >= 2 && !keywords.has(clean.toLowerCase())) {
        result.tokenQuery = clean.toUpperCase();
        break;
      }
    }

    return result;
  }, []);

  useEffect(() => {
    const parsed = parseCommand(value);
    setLastParsed(parsed);
    onParsedCommand(parsed);
  }, [value, parseCommand, onParsedCommand]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && value.trim()) {
      onExecute();
      setValue('');
    }
  };

  const chips: { label: string; value: string; color: string }[] = [];
  if (lastParsed.action === 'long' || lastParsed.action === undefined) {
    if (lastParsed.tokenQuery) {
      chips.push({ label: 'TOKEN', value: lastParsed.tokenQuery, color: 'var(--primary)' });
    } else if (selectedTokenSymbol) {
      chips.push({ label: 'TOKEN', value: selectedTokenSymbol, color: 'var(--text-2)' });
    }
    if (lastParsed.capitalSol) {
      chips.push({ label: 'SIZE', value: `${lastParsed.capitalSol} SOL`, color: 'var(--cyan)' });
    }
    if (lastParsed.leverage) {
      chips.push({ label: 'LEV', value: `${lastParsed.leverage}x`, color: 'var(--yellow)' });
    }
    if (lastParsed.priority) {
      chips.push({ label: 'FEE', value: lastParsed.priority.toUpperCase(), color: 'var(--text-1)' });
    }
  } else if (lastParsed.action === 'close') {
    chips.push({ label: 'ACTION', value: 'CLOSE', color: 'var(--red)' });
  } else if (lastParsed.action === 'closeall') {
    chips.push({ label: 'ACTION', value: 'CLOSE ALL', color: 'var(--red)' });
  }

  return (
    <motion.div
      className="command-bar-wrapper"
      initial={{ y: 20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 0.3, duration: 0.4, ease: 'easeOut' }}
    >
      <div className={`command-bar ${focused ? 'command-bar-focused' : ''}`}>
        <span className="command-bar-prefix">FRONT</span>
        <span className="command-bar-caret">&gt;</span>
        <input
          ref={inputRef}
          className="command-bar-input"
          type="text"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onKeyDown={handleKeyDown}
          placeholder={focused ? 'open 0.5 sol on POPCAT at 7x fast' : 'Press Cmd+K to trade'}
          spellCheck={false}
          autoComplete="off"
        />
        {!focused && (
          <kbd className="command-bar-kbd">
            {navigator.platform?.includes('Mac') ? 'Cmd' : 'Ctrl'} K
          </kbd>
        )}
      </div>

      <AnimatePresence>
        {chips.length > 0 && focused && (
          <motion.div
            className="command-bar-chips"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            transition={{ duration: 0.15 }}
          >
            {chips.map((chip) => (
              <span key={chip.label} className="command-chip" style={{ borderColor: chip.color }}>
                <span className="command-chip-label">{chip.label}</span>
                <span className="command-chip-value" style={{ color: chip.color }}>{chip.value}</span>
              </span>
            ))}
            {value.trim() && (
              <span className="command-chip-hint">
                Press Enter to execute
              </span>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
